import { AIONZ_DIALOG_DATA, AionzDialogModule, AionzDialogRef, HeaderBottomSheetComponent } from '@aionzdev/design-system';
import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, signal, viewChild } from '@angular/core';
import { finalize, take } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer } from '@angular/platform-browser';
import {
  PurchaseRequestDocumentUploadDialogAction,
} from '../../../interfaces/purchase-request-document-upload-dialog-result';
import { PurchaseRequestDocumentUploadDialogData } from '../../../interfaces/purchase-request-document-upload-dialog-data';
import {
  PurchaseRequestDocumentUploadItemResult,
  PurchaseRequestDocumentUploadItemType
} from '../../../interfaces/purchase-request-document-upload-item-result';
import { AbstractPurchaseRequestDocumentService } from '../../../services/abstract-purchase-request-document-upload.service';

type UploadFieldKey = 'spreadsheet' | 'quote';

interface UploadFieldState {
  readonly key: UploadFieldKey;
  readonly order: number;
  readonly label: string;
  readonly placeholder: string;
  readonly maxSizeLabel: string;
  readonly svgIcon: string;
  readonly accept: string;
  readonly maxSizeInBytes: number;
  readonly file: File | null;
  readonly isDragOver: boolean;
  readonly errors: string[];
}

interface UploadFieldViewModel extends UploadFieldState {
  readonly formattedSize: string | null;
}

@Component({
  selector: 'aionz-purchase-request-document-upload-dialog',
  imports: [AionzDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, HeaderBottomSheetComponent],
  templateUrl: './purchase-request-document-upload-dialog.component.html',
  styleUrl: './purchase-request-document-upload-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PurchaseRequestDocumentUploadDialogComponent {
  private readonly dialogRef = inject(AionzDialogRef<PurchaseRequestDocumentUploadDialogComponent, void>);
  private readonly uploadService = inject(AbstractPurchaseRequestDocumentService);
  private readonly data = inject<PurchaseRequestDocumentUploadDialogData | null>(AIONZ_DIALOG_DATA, {
    optional: true
  });
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly domSanitizer = inject(DomSanitizer);

  protected readonly spreadsheetInput = viewChild.required<ElementRef<HTMLInputElement>>('spreadsheetInput');
  protected readonly quoteInput = viewChild.required<ElementRef<HTMLInputElement>>('quoteInput');

  private readonly uploadFields = signal<Record<UploadFieldKey, UploadFieldState>>({
    spreadsheet: {
      key: 'spreadsheet',
      order: 1,
      label: this.data?.spreadsheetLabel ?? 'Planilha preenchida',
      placeholder:
        this.data?.spreadsheetPlaceholder ??
        'Arraste e solte sua planilha aqui ou clique para selecionar',
      maxSizeLabel: this.data?.spreadsheetMaxSizeLabel ?? 'Tamanho máximo: 5MB',
      svgIcon: 'xls-icon',
      accept: '.xlsx,.xls,.csv',
      maxSizeInBytes: 5 * 1024 * 1024,
      file: null,
      isDragOver: false,
      errors: []
    },
    quote: {
      key: 'quote',
      order: 2,
      label: this.data?.quoteLabel ?? 'Orçamento do Fornecedor (PDF)',
      placeholder:
        this.data?.quotePlaceholder ??
        'Arraste e solte o PDF do orçamento aqui ou clique para selecionar',
      maxSizeLabel: this.data?.quoteMaxSizeLabel ?? 'Tamanho máximo: 10MB',
      svgIcon: 'pdf-icon',
      accept: '.pdf,application/pdf',
      maxSizeInBytes: 10 * 1024 * 1024,
      file: null,
      isDragOver: false,
      errors: []
    }
  });

  protected readonly isSubmitting = signal(false);
  protected readonly title = this.data?.title ?? 'Upload de Documentos';

  protected readonly subtitle = this.data?.subtitle;
  protected readonly fields = computed<UploadFieldViewModel[]>(() => [
    this.toViewModel(this.uploadFields().spreadsheet),
    this.toViewModel(this.uploadFields().quote)
  ]);

  protected readonly canSubmit = computed(() => {
    const fields = this.uploadFields();
    return !!fields.spreadsheet.file && !!fields.quote.file;
  });

  protected readonly showUploadedFilesNotice = computed(() => this.canSubmit());

  constructor() {
    this.iconRegistry.addSvgIcon(
      'xls-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/xls-Icon.svg')
    );
    this.iconRegistry.addSvgIcon(
      'pdf-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/pdf-icon.svg')
    );
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected goBack(): void {
    this.dialogRef.close({ action: PurchaseRequestDocumentUploadDialogAction.BACK });
  }

  protected submit(): void {
    const fields = this.uploadFields();
    if (!fields.spreadsheet.file || !fields.quote.file || this.isSubmitting()) {
      return;
    }

    this.clearFieldErrors();
    this.isSubmitting.set(true);

    const purchaseId = this.data?.purchaseId;
    const supplierId = this.data?.supplierId;

    if (!purchaseId || !supplierId) {
      this.isSubmitting.set(false);
      throw new Error('purchaseId and supplierId are required to upload documents.');
    }

    this.uploadService
      .uploadDocuments({
        purchaseId,
        supplierId,
        spreadsheetFile: fields.spreadsheet.file,
        supplierQuoteFile: fields.quote.file
      })
      .pipe(
        take(1),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe(results => {
        this.applyUploadResults(results);

        if (results.every(result => result.success)) {
          this.dialogRef.close({ action: PurchaseRequestDocumentUploadDialogAction.SUCCESS });
        }
      });
  }

  protected openFilePicker(fieldKey: UploadFieldKey, event?: Event): void {
    event?.stopPropagation();
    const input = fieldKey === 'spreadsheet' ? this.spreadsheetInput() : this.quoteInput();
    input.nativeElement.click();
  }

  protected onDropzoneKeydown(event: KeyboardEvent, fieldKey: UploadFieldKey): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.openFilePicker(fieldKey);
  }

  protected onDragOver(event: DragEvent, fieldKey: UploadFieldKey): void {
    event.preventDefault();
    event.stopPropagation();
    this.patchField(fieldKey, { isDragOver: true });
  }

  protected onDragLeave(event: DragEvent, fieldKey: UploadFieldKey): void {
    event.preventDefault();
    event.stopPropagation();
    this.patchField(fieldKey, { isDragOver: false });
  }

  protected onDrop(event: DragEvent, fieldKey: UploadFieldKey): void {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer?.files?.[0] ?? null;
    this.patchField(fieldKey, { isDragOver: false });

    if (file) {
      this.handleSelectedFile(fieldKey, file);
    }
  }

  protected onFileChange(event: Event, fieldKey: UploadFieldKey): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (file) {
      this.handleSelectedFile(fieldKey, file);
    }

    input.value = '';
  }

  protected clearFile(event: Event, fieldKey: UploadFieldKey): void {
    event.stopPropagation();
    this.patchField(fieldKey, { file: null, errors: [] });
  }

  private handleSelectedFile(fieldKey: UploadFieldKey, file: File): void {
    const field = this.uploadFields()[fieldKey];
    let error: string | null = null;

    if (!this.isValidFileType(fieldKey, file)) {
      error = 'Formato de arquivo não suportado.';
    } else if (file.size === 0) {
      error = 'O arquivo está vazio.';
    } else if (file.size > field.maxSizeInBytes) {
      error = `O arquivo excede o tamanho máximo permitido (${this.formatFileSize(field.maxSizeInBytes)}).`;
    }

    if (error) {
      this.patchField(fieldKey, { file: null, errors: [error] });
      return;
    }

    this.patchField(fieldKey, { file, errors: [] });
  }

  private isValidFileType(fieldKey: UploadFieldKey, file: File): boolean {
    if (fieldKey === 'quote') {
      return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    }

    const extension = file.name.toLowerCase();
    return extension.endsWith('.xlsx') || extension.endsWith('.xls') || extension.endsWith('.csv');
  }

  private patchField(fieldKey: UploadFieldKey, patch: Partial<UploadFieldState>): void {
    this.uploadFields.update(fields => ({
      ...fields,
      [fieldKey]: {
        ...fields[fieldKey],
        ...patch
      }
    }));
  }

  private toViewModel(field: UploadFieldState): UploadFieldViewModel {
    return {
      ...field,
      formattedSize: field.file ? this.formatFileSize(field.file.size) : null
    };
  }

  private formatFileSize(sizeInBytes: number): string {
    const sizeInKb = sizeInBytes / 1024;
    if (sizeInKb < 1024) {
      return `${Math.round(sizeInKb)} KB`;
    }

    const sizeInMb = sizeInKb / 1024;
    return `${Number(sizeInMb.toFixed(1))}MB`;
  }

  private clearFieldErrors(): void {
    this.uploadFields.update(fields => ({
      spreadsheet: { ...fields.spreadsheet, errors: [] },
      quote: { ...fields.quote, errors: [] }
    }));
  }

  private applyUploadResults(results: PurchaseRequestDocumentUploadItemResult[]): void {
    for (const result of results) {
      const fieldKey = this.mapItemTypeToFieldKey(result.type);
      this.patchField(fieldKey, { errors: result.success ? [] : result.errors ?? [] });
    }
  }

  private mapItemTypeToFieldKey(type: PurchaseRequestDocumentUploadItemType): UploadFieldKey {
    return type === PurchaseRequestDocumentUploadItemType.SPREADSHEET ? 'spreadsheet' : 'quote';
  }
}
